import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormGroup,
  FormControlLabel,
  Switch,
  Checkbox,
  ListItemText,
  Alert,
  Typography,
} from "@mui/material";
import { useEffect, useState, useCallback, useRef } from "react";
import { queryClient } from "../../lib/queryClient";
import { mutateUser, useUser } from "../../hooks/useUser";
import { usePermissionService } from "../../services/permissionService";
import GeoTooltip from "../GeoTooltip/GeoTooltip";
import { useDrfadminNationsList } from "../../api-client/drfadmin/drfadmin";
import { useDrfadminUsersPartialUpdate } from "../../api-client/drfadmin/drfadmin";
import { useRolesList } from "../../api-client/roles/roles";
import {
  useAccountSwitchListClassesList,
  useAccountSwitchSelectClassCreate
} from "../../api-client/account-switch/account-switch";
import type { ClassNameListResponse } from "../../api-client/models/classNameListResponse";

// Constants
const SPACING = 2;
const MIN_WIDTH = 200;

interface AdminHeaderProps {
  menuOpen: boolean;
  onClose?: () => void;
}

export default function AdminHeader({ menuOpen, onClose }: AdminHeaderProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = useUser();
  const superAdmin = import.meta.env.VITE_APP_ENV == "development" || user.isSuperuserImpersonation;
  const permissionService = usePermissionService();
  const [adminOn, setAdminOn] = useState(permissionService.getAdminOn());
  const [className, setClassName] = useState(user.className.id);
  const [nation, setNation] = useState(user.nation.id);
  const [selectedRoles, setSelectedRoles] = useState<number[]>(
    user.roles?.map((role) => role.id) || [],
  );
  const [rolesDropdownOpen, setRolesDropdownOpen] = useState(false);
  const previousRolesRef = useRef<number[]>(selectedRoles);
  const [justSaved, setJustSaved] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // TanStack Query hooks
  const { data: availableClasses } = useAccountSwitchListClassesList();
  const { data: nations } = useDrfadminNationsList();
  const { data: allRoles } = useRolesList();

  // Mutations
  const { mutateAsync: updateUserMutation } = useDrfadminUsersPartialUpdate();
  const { mutateAsync: selectClassMutation } = useAccountSwitchSelectClassCreate();

  // Sync local state whenever the server's account/className/nation/roles
  // move out from under us (account switching, or an out-of-band change to
  // this user picked up by a refetch) - tracks the same 4 triggers the
  // original effect's dependency array covered, not just account.id, since
  // the auto-save effects below would otherwise push stale local state back
  // to the server and silently revert a legitimate external change.
  const [prevAccountId, setPrevAccountId] = useState(user.account.id);
  const [prevClassNameId, setPrevClassNameId] = useState(user.className.id);
  const [prevNationId, setPrevNationId] = useState(user.nation.id);
  const [prevRoles, setPrevRoles] = useState(user.roles);
  if (
    user.account.id !== prevAccountId ||
    user.className.id !== prevClassNameId ||
    user.nation.id !== prevNationId ||
    user.roles !== prevRoles
  ) {
    setPrevAccountId(user.account.id);
    setPrevClassNameId(user.className.id);
    setPrevNationId(user.nation.id);
    setPrevRoles(user.roles);
    setClassName(user.className.id);
    setNation(user.nation.id);
    setSelectedRoles(user.roles?.map((role) => role.id) || []);
  }
  // previousRolesRef must only be updated in lockstep with the reset above
  // (not on every selectedRoles change), so it stays a ref sync keyed on the
  // same triggers rather than folded into the render-time block.
  useEffect(() => {
    previousRolesRef.current = selectedRoles;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevAccountId, prevClassNameId, prevNationId, prevRoles]);

  const performUpdate = useCallback(async (
    classId: number,
    nationId: number,
    roleIds: number[]
  ) => {
    if (!user || saving) return;

    setError(null);
    setSaving(true);

    try {
      await updateUserMutation({
        id: user.id,
        data: {
          className: classId,
          nation: nationId,
          roles: roleIds,
        },
      });

      // Invalidate all queries and update user
      await mutateUser();
      await queryClient.invalidateQueries();

      // Success - no need to reload page
      setError(null);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      setJustSaved(true);
      saveTimeoutRef.current = setTimeout(() => {
        setJustSaved(false);
        // Close the header after showing "Saved" message
        if (onClose) {
          onClose();
        }
      }, 0);
    } catch (err) {
      console.error("Error updating admin options:", err);
      setError("Failed to update admin options. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [user, saving, updateUserMutation, onClose]);

  // Auto-save when className changes - use select endpoint
  useEffect(() => {
    if (menuOpen && user.className.id !== className && !saving) {
      const selectClass = async () => {
        setError(null);
        setSaving(true);

        try {
          // Call the select endpoint to switch classes (now uses className ID directly)
          await selectClassMutation({ id: className });

          // Invalidate all queries and update user
          await mutateUser();
          await queryClient.invalidateQueries();

          // Success
          setError(null);
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
          setJustSaved(true);
          saveTimeoutRef.current = setTimeout(() => {
            setJustSaved(false);
            if (onClose) {
              onClose();
            }
          }, 0);
        } catch (err) {
          console.error("Error selecting game:", err);
          setError("Failed to switch game. Please try again.");
        } finally {
          setSaving(false);
        }
      };

      void selectClass();
    }
  }, [menuOpen, className, user.className.id, saving, selectClassMutation, onClose]);

  // Auto-save when nation changes. performUpdate synchronizes with the backend
  // (an external system), which is what effects are for; the lint rule can't
  // see past the useCallback to know performUpdate's setState calls guard a
  // real network request rather than a plain derived-state update.
  useEffect(() => {
    if (menuOpen && user.nation.id !== nation && !saving) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      performUpdate(className, nation, selectedRoles);
    }
  }, [menuOpen, nation, user.nation.id, className, selectedRoles, saving, performUpdate]);

  // Auto-save when roles change (after dropdown closes)
  useEffect(() => {
    if (!menuOpen || rolesDropdownOpen) return;

    const currentRoleIds = (user.roles || []).map((role) => role.id).sort();
    const selectedRoleIds = [...selectedRoles].sort();
    const hasRolesChanged =
      currentRoleIds.length !== selectedRoleIds.length ||
      !currentRoleIds.every((id, index) => id === selectedRoleIds[index]);

    const prevRoleIds = [...previousRolesRef.current].sort();
    const rolesDifferentFromPrevious =
      prevRoleIds.length !== selectedRoleIds.length ||
      !prevRoleIds.every((id, index) => id === selectedRoleIds[index]);

    if (hasRolesChanged && rolesDifferentFromPrevious && !saving) {
      performUpdate(className, nation, selectedRoles);
      previousRolesRef.current = selectedRoles;
    }
  }, [menuOpen, rolesDropdownOpen, selectedRoles, user.roles, className, nation, saving, performUpdate]);

  const updateClass = (event: SelectChangeEvent) => {
    setClassName(parseInt(event.target.value));
  };

  const getCurrentRoleNames = () => {
    if (!selectedRoles.length) return "No Roles";
    const roleNames = selectedRoles.map((roleId) => {
      const role = allRoles?.find((r) => r.id === roleId);
      return role ? role.name : `Role ${roleId}`;
    });
    return roleNames.join(", ");
  };

  return menuOpen ? (
    <Box
      sx={{
        bgcolor: "grey.200",
        minHeight: "80px",
        flexWrap: "wrap",
        width: "100%",
        display: "flex",
        alignItems: "center",
        px: SPACING,
        py: 1,
        gap: SPACING,
        borderBottom: 1,
        borderColor: "divider",
      }}
      role="region"
      aria-label="Admin controls"
    >
      {error && (
        <Alert severity="error" sx={{ width: "100%", mb: 1 }}>
          {error}
        </Alert>
      )}

      <FormControl sx={{ ml: SPACING, minWidth: MIN_WIDTH }}>
        <InputLabel>Game Select</InputLabel>
        <Select
          label="Game Select"
          value={className.toString()}
          onChange={updateClass}
          disabled={saving}
          aria-label="Select game"
        >
          {availableClasses?.sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((cls: ClassNameListResponse) => (
            <MenuItem key={cls.id} value={cls.id}>
              {cls.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ ml: SPACING, minWidth: MIN_WIDTH }}>
        <InputLabel>Nation Select</InputLabel>
        <Select
          label="Nation Select"
          value={nation.toString()}
          onChange={(e) => setNation(parseInt(e.target.value))}
          disabled={saving}
          aria-label="Select nation"
        >
          {nations?.results?.filter((n) => n.className === className).map((nat) => (
            <MenuItem key={nat.id} value={nat.id}>
              {nat.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {superAdmin && (<>
        <FormControl sx={{ ml: SPACING, minWidth: MIN_WIDTH }}>
          <InputLabel>Assigned Roles</InputLabel>
          <Select
            label="Assigned Roles"
            multiple
            open={rolesDropdownOpen}
            onOpen={() => setRolesDropdownOpen(true)}
            onClose={() => setRolesDropdownOpen(false)}
            value={selectedRoles}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedRoles(typeof value === "string" ? [] : value);
            }}
            disabled={saving}
            aria-label="Select assigned roles"
            renderValue={(selected) => {
              if (selected.length === 0) return <em>No Roles</em>;
              const roleNames = selected.map((roleId) => {
                const role = allRoles?.find((r) => r.id === roleId);
                return role ? role.name : `Role ${roleId}`;
              });
              return roleNames.join(", ");
            }}
          >
            {allRoles?.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                <Checkbox checked={selectedRoles.indexOf(role.id) > -1} />
                <ListItemText primary={role.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <GeoTooltip
          title={`Current roles: ${getCurrentRoleNames()}. You have ${selectedRoles.length} role(s) selected. Select/deselect roles to change your permissions and capabilities.`}
        />

        <FormGroup sx={{ ml: SPACING }}>
          <FormControlLabel
            control={
              <Switch
                checked={!adminOn}
                onChange={async () => {
                  setSaving(true);
                  setAdminOn(!adminOn);
                  permissionService.setAdminOn(!adminOn);
                  await queryClient.invalidateQueries();
                  setSaving(false);
                }}
                disabled={saving}
                aria-label="Toggle admin mode"
              />
            }
            label="Disable Admin"
          />
        </FormGroup>
        <GeoTooltip title="As an admin you often have options which may not be available to other users. This switch will disable those options so you can view the site as a regular user." />
        </>)
      }
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 60, ml: SPACING }}>
        {saving && <CircularProgress size={20} />}
        {!saving && justSaved && (
          <Typography sx={{ color: "success.main", fontWeight: 600 }}>
            Saved
          </Typography>
        )}
      </Box>
    </Box>
  ) : null;
}
